import type { AdminCertification, KeywordPracticeConfig } from "@/lib/certificationStore";
import { resolveChapterPracticeConfig } from "@/lib/certificationStore";

/** User-facing certification shape derived from the admin store. */
export interface UserCertification {
  id: string;
  code: string;
  name: string;
  fullName: string;
  version: string;
  level: "Foundation" | "Advanced" | "Expert" | "N/A";
  description: string;
  questionsPerExam: number;
  totalPoints: number;
  passingScorePoints: number;
  timeLimitMinutes: number;
  extendedTimeMinutes: number;
  requiredTier: "free" | "monthly" | "quarterly" | "yearly";
  resources: { title: string; version: string; url: string }[];
}

/** Map AdminCertification → UserCertification for user-facing pages. */
export function toUserCert(c: AdminCertification): UserCertification {
  const totalPoints = c.totalPossiblePoints ?? (
    c.passPercentage > 0
      ? Math.round(c.passingScorePoints / (c.passPercentage / 100))
      : c.passingScorePoints
  );

  return {
    id: c.certificationId,
    code: c.code,
    name: c.name,
    fullName: c.fullName || c.name,
    version: c.version,
    level: c.level,
    description: c.description || "",
    questionsPerExam: c.questionsPerExam ?? c.questions,
    totalPoints,
    passingScorePoints: c.passingScorePoints,
    timeLimitMinutes: c.timeMinutes,
    extendedTimeMinutes: c.timeMinutes + (c.extensionMinutes || 0),
    requiredTier: c.requiredTier ?? "free",
    resources: c.resources || [],
  };
}

/**
 * Returns the set of unique keyword IDs that are practicable for a given chapter.
 * Flashcards and Match use all assigned keywords. Categorize only uses keywords
 * with valid category assignments. Returns the union across enabled modes.
 */
export function getPracticableKeywordIds(
  certConfig: KeywordPracticeConfig,
  chapterId: string,
  allAssignedKeywordIds: string[]
): Set<string> {
  const resolved = resolveChapterPracticeConfig(certConfig, chapterId);
  const ids = new Set<string>();

  // Flashcards and Match use all assigned keywords
  if (resolved.flashcardsEnabled || resolved.matchEnabled) {
    allAssignedKeywordIds.forEach(id => ids.add(id));
    return ids;
  }

  // Only Categorize enabled: only keywords with category assignments
  if (resolved.categorizeEnabled) {
    const included = new Set(resolved.categorizeIncludedKeywordIds);
    for (const id of allAssignedKeywordIds) {
      if (included.has(id) && resolved.categoryAssignments[id]) {
        ids.add(id);
      }
    }
  }

  // If no modes enabled, no keywords are practicable
  return ids;
}
